import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Datatable from "../../../../core/common/dataTable";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import { all_routes } from "../../../router/all_routes";
import PdfIcon from "../../../../style/icon/svg/PdfIcon";
import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
import Footer from "../../../../core/common/Footer";
import useApi from "../../../../core/customhook/useApi";
import { apibaseurl } from "../../../../core/webservice/Webservice_url";
import Swal from "sweetalert2";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
import { formatDate } from "../../../../core/common/TabelValue";
import { useLocation, useNavigate } from "react-router";
import { PageLoader } from "../../../../core/common/loader/PageLoader";
import Loading from "../../../../core/common/custom-loading/Loading";
import AddressDetailsView from "./Addressdetailsview";

// One form entry (single check = 1 entry, multiple check = many entries)
interface FormEntry {
  entry_index: number;
  education_type?: string | null;
  fields: any[];
}

// Field types that are shown in Form 1 as read-only inputs
const FORM1_FIELD_TYPES = ["text", "number", "dropdown", "date", "email"];

/**
 * API can return the form in two shapes:
 *  - data.fields  -> single entry
 *  - data.entries -> [{ entry_index, fields: [] }, ...] (single or multiple)
 * This normalises both into an array of entries.
 */
const getFormEntries = (data: any): FormEntry[] => {
  if (!data) return [];

  if (Array.isArray(data.entries) && data.entries.length > 0) {
    return data.entries
      .filter((entry: any) => Array.isArray(entry?.fields))
      .map((entry: any, index: number) => ({
        entry_index: entry.entry_index ?? index,
        education_type: entry.education_type ?? null,
        fields: entry.fields,
      }));
  }

  if (Array.isArray(data.fields) && data.fields.length > 0) {
    return [{ entry_index: 0, fields: data.fields }];
  }

  return [];
};

const formatEducationType = (value?: string | null) =>
  value
    ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "";

const InsufficiencyDetails = () => {
  const location = useLocation();
  const [showGuide, setShowGuide] = useState<boolean>(true);
  const [templateInfo, setTemplateInfo] = useState<any>(null);
  const [docsInfo, setDocsInfo] = useState<any[]>([]);
  const [formEntries, setFormEntries] = useState<FormEntry[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [insuffHistroy, setInsuffHistory] = useState<File[]>([]);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [addressData, setAddressData] = useState<any[]>([]);

  const { assignment_template_id, assignment_id, case_id, candidate_id } =
    location?.state || {};

  const getTemplateForm = useApi(
    `/api/v1/insufficiency/template/${assignment_template_id}`,
    {
      autoCall: false,
      method: "GET",
      handleGlobalError: false,
    },
  );

  const getTemplateDocument = useApi(
    `/api/v1/insufficiency/documents/${assignment_template_id}`,
    {
      autoCall: false,
      method: "GET",
      handleGlobalError: false,
    },
  );

  const insuffHistoryAPI = useApi(
    `/api/v1/insufficiency/check/${assignment_template_id}`,
    {
      autoCall: false,
      method: "GET",
      handleGlobalError: false,
    },
  );

  const getTemplate = async () => {
    try {
      const res = await getTemplateForm.callApi();

      if (res?.success) {
        const data = res.data;

        // Store response details except fields
        setTemplateInfo({
          response_id: data.response_id,
          assignment_template_id: data.assignment_template_id,
          is_draft: data.is_draft,
          submitted_at: data.submitted_at,
          updated_at: data.updated_at,
          check_name: data.check_name,
          form_type: data.form_type,
        });
        setAddressData(Array.isArray(data.data) ? data.data : []);

        // entries (single / multiple) -> fields sorted by display_order
        setFormEntries(
          getFormEntries(data).map((entry) => ({
            ...entry,
            fields: [...entry.fields].sort(
              (a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0),
            ),
          })),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getTemplateDocs = async () => {
    try {
      const res = await getTemplateDocument.callApi();

      if (res?.success) {
        setDocsInfo(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const insuffHistory = async () => {
    try {
      const res = await insuffHistoryAPI.callApi();

      if (res?.success) {
        setInsuffHistory(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!assignment_template_id) return;
    getTemplate();
    getTemplateDocs();
    insuffHistory();
  }, [assignment_template_id]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setDocuments(Array.from(e.target.files));
  };

  const columns = [
    {
      title: "Case ID",
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a: any, b: any) =>
        (a?.case_id || "").localeCompare(b?.case_id || ""),
    },

    {
      title: "Insufficiency Raise Date",
      dataIndex: "created_at",
      key: "created_at",
      onCell: () => cellWidth(190),
      render: (value: string) => formatDate(value),
      sorter: (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },

    {
      title: "Due Date",
      dataIndex: "actor_due_date",
      key: "actor_due_date",
      onCell: () => cellWidth(190),
      render: (value: string) => formatDate(value),
      sorter: (a: any, b: any) =>
        new Date(a.actor_due_date).getTime() -
        new Date(b.actor_due_date).getTime(),
    },

    {
      title: "User Type",
      dataIndex: "actor_type",
      key: "actor_type",
      onCell: () => cellWidth(150),
      render: (actorType: string) => (
        <span
          className={`badge px-3 py-2 ${
            actorType === "CANDIDATE"
              ? "bg-secondary-subtle text-secondary-emphasis"
              : "bg-primary-subtle text-primary-emphasis"
          }`}
          style={{
            fontSize: "12px",
            fontWeight: 700,
            minWidth: "100px",
          }}
        >
          {actorType}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a?.actor_type || "").localeCompare(b?.actor_type || ""),
    },
    {
      title: "Raised By",
      dataIndex: "actor_name",
      key: "actor_name",
      onCell: () => cellWidth(150),
      render: (text: string) => <div className="text-capitalize">{text}</div>,
      sorter: (a: any, b: any) =>
        (a?.actor_name || "").localeCompare(b?.actor_name || ""),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      onCell: () => cellWidth(350),
      render: (text: string) => text || "-",
      sorter: (a: any, b: any) =>
        (a?.remarks || "").localeCompare(b?.remarks || ""),
    },
    {
      title: "Candidate Name",
      dataIndex: "candidate_name",
      key: "candidate_name",
      onCell: () => cellWidth(180),
      sorter: (a: any, b: any) =>
        (a?.candidate_name || "").localeCompare(b?.candidate_name || ""),
    },
    {
      title: "Documents",
      dataIndex: "documents",
      key: "documents",
      align: "center",
      width: 120,
      render: (documents: any[], record: any) => (
        <button
          className="btn btn-sm"
          title="View Documents"
          onClick={() => {
            setSelectedDocuments(documents);
            setShowDocumentsModal(true);
          }}
        >
          <i className="ti ti-eye text-primary fs-3"></i>
        </button>
      ),
    },
  ];

  const breadcrumbItem = [
    // { label: "Live Case Progress" },
    { label: "Case Details" },
    { label: "Insufficiency Details" },
  ];

  // Form 1 entries: sirf wahi fields jo dikhane hain (already sorted by display_order in getTemplate)
  const form1Entries = formEntries.map((entry) => ({
    ...entry,
    fields: entry.fields.filter((field) =>
      FORM1_FIELD_TYPES.includes(field?.field_type),
    ),
  }));

  const hasForm1Fields = form1Entries.some((entry) => entry.fields.length > 0);
  const isMultipleEntries = form1Entries.length > 1;

  // AddressDetailsView sirf "Address Verification" check par dikhega
  const isAddressCheck =
    templateInfo?.check_name?.trim().toLowerCase() === "address verification";

  // Uploaded Documents card tabhi dikhega jab documents hon (aur address check na ho)
  const showDocsCard =
    !!templateInfo?.check_name && !isAddressCheck && docsInfo?.length > 0;

  // Single field (read-only)
  const renderForm1Field = (field: any, entryKey: number) => (
    <div className="col-md-4 mb-3" key={`${entryKey}-${field.field_id}`}>
      <label className="form-label fw-semibold">
        {field.label}
        {field.is_required && <span className="text-danger ms-1">*</span>}
      </label>

      {/* Text / Email */}
      {(field.field_type === "text" || field.field_type === "email") && (
        <input
          type="text"
          className="form-control"
          value={String(field.value ?? "").trim() || "Not Provided"}
          placeholder={field.placeholder}
          disabled
        />
      )}

      {/* Date */}
      {field.field_type === "date" && (
        <input
          type="text"
          className="form-control"
          value={
            field.value && dayjs(field.value).isValid()
              ? dayjs(field.value).format("DD MMM YYYY")
              : "Not Provided"
          }
          placeholder={field.placeholder}
          disabled
        />
      )}

      {/* Number */}
      {field.field_type === "number" && (
        <input
          type="number"
          className="form-control"
          value={field.value || ""}
          placeholder={field.value ? field.placeholder : "Not Provided"}
          disabled
        />
      )}

      {/* Dropdown */}
      {field.field_type === "dropdown" && (
        <select className="form-select" value={field.value || ""} disabled>
          <option value="">Not Selected</option>

          {Array.isArray(field.options) &&
            field.options.map((option: any, index: number) => {
              if (typeof option === "string") {
                return (
                  <option key={option} value={option}>
                    {option}
                  </option>
                );
              }

              return (
                <option key={option.value ?? index} value={option.value ?? ""}>
                  {option.label ?? option.value}
                </option>
              );
            })}
        </select>
      )}
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content px-2 px-md-4 pb-5">
        {insuffHistoryAPI.loading ? (
          <PageLoader />
        ) : (
          <>
            {/* PageBreadcrumb */}
            <PageBreadcrumb
              title="Insufficieny Details"
              breadcrumbs={breadcrumbItem}
              showBackButton={true}
              backRoute={-1}
              // backRoute={() => navigate(-1)}
              // showGuideButton={true}
              // onGuideClick={() => setShowGuide((prev) => !prev)}
            />

            <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
              <button
                className="btn border bg-white px-3"
                onClick={() => setShowGuide((prev) => !prev)}
              >
                <i className="ti ti-info-circle-filled fs-16"></i> Guide
              </button>
            </div>
            {showGuide && (
              <div
                style={{
                  overflow: "hidden",
                  maxHeight: showGuide ? "600px" : "0px",
                  transition: "all 0.35s ease-in-out",
                }}
              >
                {/* Arrow */}
                <div className="text-end pe-4">
                  <i
                    className="ti ti-triangle-inverted-filled"
                    style={{
                      fontSize: "22px",
                      color: "#d6d6d6",
                      lineHeight: 0,
                    }}
                  />
                </div>

                {/* Card */}
                <div
                  className="card border-0 shadow-sm"
                  style={{
                    borderRadius: "10px",
                    background: "#fff",
                  }}
                >
                  <div className="card-body p-4 position-relative">
                    {/* Close */}
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute"
                      style={{
                        top: "15px",
                        right: "15px",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                      }}
                      onClick={() => setShowGuide(false)}
                    >
                      <i className="ti ti-x"></i>
                    </button>

                    {/* Heading */}
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: "#fff3cd",
                        }}
                      >
                        <i
                          className="ti ti-alert-circle"
                          style={{
                            color: "#d97706",
                            fontSize: "20px",
                          }}
                        />
                      </div>

                      <h5 className="mb-1 fw-bold">Insufficiency</h5>
                    </div>

                    {/* Form 1 */}
                    <div className="mb-4">
                      <h6 className="fw-semibold text-primary mb-2">Form 1</h6>

                      <div className="alert alert-light text-dark border mb-0">
                        <i className="ti ti-info-circle me-2 text-primary"></i>
                        Please review the check(s) listed below before
                        proceeding.
                      </div>
                    </div>

                    {/* Form 2 */}
                    <div>
                      <h6 className="fw-semibold text-primary mb-2">Form 2</h6>

                      <div className="alert alert-light text-dark border mb-3">
                        <ul className="mb-0 ps-3">
                          <li className="mb-2">
                            Mention your remarks in{" "}
                            <strong>Insufficiency Details</strong> section and
                            provide the required documentation.Please review
                            form 1 and the documents submitted by you and then
                            submit the insufficiency by providing the
                            information in the form below.
                          </li>

                          <li className="mb-2">
                            In case you feel that insufficiency request is
                            incorrect and the details are already being
                            provided, click on the incorrect insuff button.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Insufficiency History */}
            <div className="card mt-3">
              <div className="card-header d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0 fw-bold">
                  <i className="ti ti-history me-2 text-primary"></i>
                  Insufficiency History
                </h5>
              </div>
              <div className="card-body">
                {/* Table */}
                <div className="">
                  <Datatable
                    dataSource={insuffHistroy}
                    columns={columns}
                    Selection={false}
                    onRow={(record: any) => ({
                      style: {
                        backgroundColor:
                          record.actor_type === "CANDIDATE"
                            ? "#f8f9fa"
                            : "#ffffff",
                      },
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Form1 Details */}
            <div className="card mt-3 ">
              <div className="card-header bg-white py-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="ti ti-clipboard-text text-warning fs-3"></i>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-0">Form - 1</h6>

                      <small className="text-muted">
                        Candidate submitted information for verification
                      </small>
                    </div>
                  </div>

                  <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle px-3 py-2">
                    {templateInfo?.check_name}
                  </span>
                </div>
              </div>

              {getTemplateForm.loading ? (
                <SnipperLoader />
              ) : isAddressCheck ? (
                <AddressDetailsView data={addressData} />
              ) : (
                <div className="card-body">
                  {!hasForm1Fields ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-4 text-center">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-muted mb-2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12h6m-6 4h6M9 8h1M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2h-3.5l-1-2h-5l-1 2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <h6 className="mb-1 text-muted fw-semibold">
                        No Record Found
                      </h6>
                      <p className="mb-0 text-muted small">
                        No verification data has been submitted for this form
                        yet.
                      </p>
                    </div>
                  ) : (
                    form1Entries.map((entry, entryIdx) => (
                      <div
                        key={entry.entry_index ?? entryIdx}
                        className={
                          isMultipleEntries &&
                          entryIdx !== form1Entries.length - 1
                            ? "mb-4"
                            : ""
                        }
                      >
                        {/* Sub heading sirf tab jab multiple entries ho */}
                        {isMultipleEntries && (
                          <div
                            className="px-3 py-2 mb-3 rounded"
                            style={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#344054",
                              background: "#F9FAFB",
                              border: "1px solid #EAECF0",
                            }}
                          >
                            {templateInfo?.check_name} ({entryIdx + 1} of{" "}
                            {form1Entries.length})
                            {entry.education_type
                              ? ` - ${formatEducationType(entry.education_type)}`
                              : ""}
                          </div>
                        )}

                        <div className="row gx-3">
                          {entry.fields.map((field) =>
                            renderForm1Field(field, entry.entry_index),
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Uploaded Documents — sirf tab dikhega jab documents hon */}
            {getTemplateForm.loading ? (
              <SnipperLoader />
            ) : showDocsCard ? (
              <div className="card mt-3">
                <div className="card-header bg-white py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="ti ti-file-text text-warning fs-3"></i>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-0">Uploaded Documents</h6>
                      <small className="text-muted">
                        Candidate uploaded supporting documents
                      </small>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="row g-3">
                    {docsInfo?.map((item) => (
                      <div
                        className="col-xl-3 col-lg-4 col-md-6"
                        key={item.file_id}
                      >
                        <div
                          className="card h-100 border-0 shadow-sm"
                          style={{
                            borderRadius: "14px",
                            transition: "0.25s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 20px rgba(0,0,0,0.12)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          <div className="card-body text-center">
                            {/* Preview */}
                            <div
                              className="mb-3 overflow-hidden rounded-3 border"
                              style={{
                                height: 150,
                                background: "#f8f9fa",
                              }}
                            >
                              {item.mime_type?.startsWith("image/") ? (
                                <img
                                  src={`${apibaseurl}/uploads/${item.s3_key}`}
                                  alt={item.original_name}
                                  className="w-100 h-100"
                                  style={{
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center h-100">
                                  <PdfIcon size={60} color="#D32F2F" />
                                </div>
                              )}
                            </div>

                            {/* Document Name */}
                            <h6
                              className="fw-bold text-capitalize mb-2"
                              title={item.field_key.replaceAll("_", " ")}
                            >
                              {item.field_key.replaceAll("_", " ")}
                            </h6>

                            {/* File Name */}
                            <div className="small text-muted text-truncate mb-3">
                              {item.original_name}
                            </div>

                            {/* Info */}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                                {item.mime_type?.split("/")[1]?.toUpperCase()}
                              </span>

                              <span className="badge bg-light text-dark border">
                                {new Date(
                                  item.uploaded_at,
                                ).toLocaleDateString()}
                              </span>
                            </div>

                            {/* View Button */}
                            <a
                              href={`${apibaseurl}/uploads/${item.s3_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary w-100 mt-2"
                            >
                              <i className="ti ti-eye me-1"></i>
                              View Document
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}

        <div
          className={`modal fade ${showDocumentsModal ? "show d-block" : ""}`}
          tabIndex={-1}
          style={{
            backgroundColor: showDocumentsModal
              ? "rgba(0,0,0,0.5)"
              : "transparent",
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              {/* Header */}
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <i className="ti ti-folder-open me-2 text-primary"></i>
                  Uploaded Documents
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDocumentsModal(false)}
                ></button>
              </div>

              {/* Body */}
              <div className="modal-body">
                {selectedDocuments.length > 0 ? (
                  <div className="row g-3">
                    {selectedDocuments.map((doc) => (
                      <div className="col-md-6" key={doc.document_id}>
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body">
                            <div className="d-flex">
                              {/* Icon */}
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
                                style={{
                                  width: 55,
                                  height: 55,
                                  flexShrink: 0,
                                }}
                              >
                                <i className="ti ti-file-text text-danger fs-3"></i>
                              </div>

                              {/* Details */}
                              <div className="flex-grow-1">
                                <h6
                                  className="fw-bold mb-1 text-truncate"
                                  title={doc.original_name}
                                >
                                  {doc.original_name}
                                </h6>

                                <div className="mb-2">
                                  <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                                    {doc.mime_type}
                                  </span>
                                </div>

                                <a
                                  href={`${apibaseurl}/uploads/${doc.file_name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-primary"
                                >
                                  <i className="ti ti-eye me-1"></i>
                                  View Document
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i
                      className="ti ti-folder-off text-secondary"
                      style={{ fontSize: "48px" }}
                    ></i>

                    <h6 className="mt-3">No Documents Found</h6>

                    <p className="text-muted mb-0">
                      No supporting documents are available.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDocumentsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default InsufficiencyDetails;


// import React, { useEffect, useState } from "react";
// import Datatable from "../../../../core/common/dataTable";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import { all_routes } from "../../../router/all_routes";
// import PdfIcon from "../../../../style/icon/svg/PdfIcon";
// import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
// import Footer from "../../../../core/common/Footer";
// import useApi from "../../../../core/customhook/useApi";
// import { apibaseurl } from "../../../../core/webservice/Webservice_url";
// import Swal from "sweetalert2";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
// import { formatDate } from "../../../../core/common/TabelValue";
// import { useLocation, useNavigate } from "react-router";
// import { PageLoader } from "../../../../core/common/loader/PageLoader";
// import Loading from "../../../../core/common/custom-loading/Loading";
// import AddressDetailsView from "./Addressdetailsview";


// const InsufficiencyDetails = () => {
//   const location = useLocation(); 
//   const [showGuide, setShowGuide] = useState<boolean>(true);
//   const [templateInfo, setTemplateInfo] = useState<any>(null);
//   const [docsInfo, setDocsInfo] = useState<any[]>([]);
//   const [fields, setFields] = useState<any[]>([]); 
//   const [documents, setDocuments] = useState<File[]>([]);
//   const [insuffHistroy, setInsuffHistory] = useState<File[]>([]);
//   const [showDocumentsModal, setShowDocumentsModal] = useState(false);
//   const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
//   const [addressData, setAddressData] = useState<any[]>([]);

//   const { assignment_template_id, assignment_id, case_id, candidate_id } =
//     location?.state || {};

//   const getTemplateForm = useApi(
//     `/api/v1/insufficiency/template/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const getTemplateDocument = useApi(
//     `/api/v1/insufficiency/documents/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const insuffHistoryAPI = useApi(
//     `/api/v1/insufficiency/check/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const getTemplate = async () => {
//     try {
//       const res = await getTemplateForm.callApi();

//       if (res?.success) {
//         const data = res.data;

//         // Store response details except fields
//         setTemplateInfo({
//           response_id: data.response_id,
//           assignment_template_id: data.assignment_template_id,
//           is_draft: data.is_draft,
//           submitted_at: data.submitted_at,
//           updated_at: data.updated_at,
//           check_name: data.check_name,
//           form_type: data.form_type,
//         });
//         setAddressData(Array.isArray(data.data) ? data.data : []);
//         setFields(
//           [...(data.fields || [])].sort(
//             (a, b) => a.display_order - b.display_order,
//           ),
//         );
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const getTemplateDocs = async () => {
//     try {
//       const res = await getTemplateDocument.callApi();

//       if (res?.success) {
//         setDocsInfo(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const insuffHistory = async () => {
//     try {
//       const res = await insuffHistoryAPI.callApi();

//       if (res?.success) {
//         setInsuffHistory(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   useEffect(() => {
//     if (!assignment_template_id) return;
//     getTemplate();
//     getTemplateDocs();
//     insuffHistory();
//   }, [assignment_template_id]);

//   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     if (!e.target.files) return;

//     setDocuments(Array.from(e.target.files));
//   };

//   const columns = [
//     {
//       title: "Case ID",
//       dataIndex: "case_id",
//       key: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a: any, b: any) =>
//         (a?.case_id || "").localeCompare(b?.case_id || ""),
//     },

//     {
//       title: "Insufficiency Raise Date",
//       dataIndex: "created_at",
//       key: "created_at",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//       sorter: (a: any, b: any) =>
//         new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
//     },

//     {
//       title: "Due Date",
//       dataIndex: "actor_due_date",
//       key: "actor_due_date",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//       sorter: (a: any, b: any) =>
//         new Date(a.actor_due_date).getTime() -
//         new Date(b.actor_due_date).getTime(),
//     },

//     {
//       title: "User Type",
//       dataIndex: "actor_type",
//       key: "actor_type",
//       onCell: () => cellWidth(150),
//       render: (actorType: string) => (
//         <span
//           className={`badge px-3 py-2 ${
//             actorType === "CANDIDATE"
//               ? "bg-secondary-subtle text-secondary-emphasis"
//               : "bg-primary-subtle text-primary-emphasis"
//           }`}
//           style={{
//             fontSize: "12px",
//             fontWeight: 700,
//             minWidth: "100px",
//           }}
//         >
//           {actorType}
//         </span>
//       ),
//       sorter: (a: any, b: any) =>
//         (a?.actor_type || "").localeCompare(b?.actor_type || ""),
//     },
//     {
//       title: "Raised By",
//       dataIndex: "actor_name",
//       key: "actor_name",
//       onCell: () => cellWidth(150),
//       render: (text: string) => <div className="text-capitalize">{text}</div>,
//       sorter: (a: any, b: any) =>
//         (a?.actor_name || "").localeCompare(b?.actor_name || ""),
//     },
//     {
//       title: "Remarks",
//       dataIndex: "remarks",
//       key: "remarks",
//       onCell: () => cellWidth(350),
//       render: (text: string) => text || "-",
//       sorter: (a: any, b: any) =>
//         (a?.remarks || "").localeCompare(b?.remarks || ""),
//     },
//     {
//       title: "Candidate Name",
//       dataIndex: "candidate_name",
//       key: "candidate_name",
//       onCell: () => cellWidth(180),
//       sorter: (a: any, b: any) =>
//         (a?.candidate_name || "").localeCompare(b?.candidate_name || ""),
//     },
//     {
//       title: "Documents",
//       dataIndex: "documents",
//       key: "documents",
//       align: "center",
//       width: 120,
//       render: (documents: any[], record: any) => (
//         <button
//           className="btn btn-sm"
//           title="View Documents"
//           onClick={() => {
//             setSelectedDocuments(documents);
//             setShowDocumentsModal(true);
//           }}
//         >
//           <i className="ti ti-eye text-primary fs-3"></i>
//         </button>
//       ),
//     },
 
//   ];
 
//   const breadcrumbItem = [
//     // { label: "Live Case Progress" },
//     { label: "Case Details" },
//     { label: "Insufficiency Details" },
//   ];

//   // filterd Form fields
//   const Filtered_form1Fields = fields?.filter((field) =>
//     ["text", "number", "dropdown"]?.includes(field.field_type),
//   );

//   // AddressDetailsView sirf "Address Verification" check par dikhega
//   const isAddressCheck =
//     templateInfo?.check_name?.trim().toLowerCase() === "address verification";

//   // Uploaded Documents card tabhi dikhega jab documents hon (aur address check na ho)
//   const showDocsCard =
//     !!templateInfo?.check_name && !isAddressCheck && docsInfo?.length > 0;

//   return (
//     <div className="page-wrapper">
//       <div className="content px-2 px-md-4 pb-5">
//         {insuffHistoryAPI.loading ? (
//           <PageLoader />
//         ) : (
//           <>
//             {/* PageBreadcrumb */}
//             <PageBreadcrumb
//               title="Insufficieny Details"
//               breadcrumbs={breadcrumbItem}
//               showBackButton={true}
//               backRoute={-1}
//               // backRoute={() => navigate(-1)}
//               // showGuideButton={true}
//               // onGuideClick={() => setShowGuide((prev) => !prev)}
//             />

//             <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
//               <button
//                 className="btn border bg-white px-3"
//                 onClick={() => setShowGuide((prev) => !prev)}
//               >
//                 <i className="ti ti-info-circle-filled fs-16"></i> Guide
//               </button>
//             </div>
//             {showGuide && (
//               <div
//                 style={{
//                   overflow: "hidden",
//                   maxHeight: showGuide ? "600px" : "0px",
//                   transition: "all 0.35s ease-in-out",
//                 }}
//               >
//                 {/* Arrow */}
//                 <div className="text-end pe-4">
//                   <i
//                     className="ti ti-triangle-inverted-filled"
//                     style={{
//                       fontSize: "22px",
//                       color: "#d6d6d6",
//                       lineHeight: 0,
//                     }}
//                   />
//                 </div>

//                 {/* Card */}
//                 <div
//                   className="card border-0 shadow-sm"
//                   style={{
//                     borderRadius: "10px",
//                     background: "#fff",
//                   }}
//                 >
//                   <div className="card-body p-4 position-relative">
//                     {/* Close */}
//                     <button
//                       type="button"
//                       className="btn btn-sm btn-light position-absolute"
//                       style={{
//                         top: "15px",
//                         right: "15px",
//                         width: "30px",
//                         height: "30px",
//                         borderRadius: "50%",
//                       }}
//                       onClick={() => setShowGuide(false)}
//                     >
//                       <i className="ti ti-x"></i>
//                     </button>

//                     {/* Heading */}
//                     <div className="d-flex align-items-center mb-4">
//                       <div
//                         className="me-3 d-flex align-items-center justify-content-center"
//                         style={{
//                           width: "42px",
//                           height: "42px",
//                           borderRadius: "50%",
//                           background: "#fff3cd",
//                         }}
//                       >
//                         <i
//                           className="ti ti-alert-circle"
//                           style={{
//                             color: "#d97706",
//                             fontSize: "20px",
//                           }}
//                         />
//                       </div>

//                       <h5 className="mb-1 fw-bold">Insufficiency</h5>
//                     </div>

//                     {/* Form 1 */}
//                     <div className="mb-4">
//                       <h6 className="fw-semibold text-primary mb-2">Form 1</h6>

//                       <div className="alert alert-light text-dark border mb-0">
//                         <i className="ti ti-info-circle me-2 text-primary"></i>
//                         Please review the check(s) listed below before
//                         proceeding.
//                       </div>
//                     </div>

//                     {/* Form 2 */}
//                     <div>
//                       <h6 className="fw-semibold text-primary mb-2">Form 2</h6>

//                       <div className="alert alert-light text-dark border mb-3">
//                         <ul className="mb-0 ps-3">
//                           <li className="mb-2">
//                             Mention your remarks in{" "}
//                             <strong>Insufficiency Details</strong> section and
//                             provide the required documentation.Please review
//                             form 1 and the documents submitted by you and then
//                             submit the insufficiency by providing the
//                             information in the form below.
//                           </li>

//                           <li className="mb-2">
//                             In case you feel that insufficiency request is
//                             incorrect and the details are already being
//                             provided, click on the incorrect insuff button.
//                           </li>
//                         </ul>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Insufficiency History */}
//             <div className="card mt-3">
//               <div className="card-header d-flex justify-content-between align-items-center py-3">
//                 <h5 className="mb-0 fw-bold">
//                   <i className="ti ti-history me-2 text-primary"></i>
//                   Insufficiency History
//                 </h5>
//               </div>
//               <div className="card-body">
//                 {/* Table */}
//                 <div className="">
//                   <Datatable
//                     dataSource={insuffHistroy}
//                     columns={columns}
//                     Selection={false}
//                     onRow={(record: any) => ({
//                       style: {
//                         backgroundColor:
//                           record.actor_type === "CANDIDATE"
//                             ? "#f8f9fa"
//                             : "#ffffff",
//                       },
//                     })}
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* Form1 Details */}
//             <div className="card mt-3 ">
//               <div className="card-header bg-white py-3 border-bottom">
//                 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-clipboard-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Form - 1</h6>

//                       <small className="text-muted">
//                         Candidate submitted information for verification
//                       </small>
//                     </div>
//                   </div>

//                   <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle px-3 py-2">
//                     {templateInfo?.check_name}
//                   </span>
//                 </div>
//               </div>

//               {getTemplateForm.loading ? (
//                 <SnipperLoader />
//               ) : isAddressCheck ? (
//                 <AddressDetailsView data={addressData} />
//               ) : (
//                 <div className="card-body">
//                   <div className="row gx-3">
//                     {/* {fields
//                       ?.filter((field) =>
//                         ["text", "number", "dropdown"]?.includes(
//                           field.field_type,
//                         ),
//                       ) */}
//                     {/* {!Filtered_form1Fields?.length && <h6 className=" mb-1">No Record Found</h6> } */}
//                     {!Filtered_form1Fields?.length && (
//                       <div className="d-flex flex-column align-items-center justify-content-center py-4 text-center">
//                         <svg
//                           width="40"
//                           height="40"
//                           viewBox="0 0 24 24"
//                           fill="none"
//                           stroke="currentColor"
//                           strokeWidth="1.5"
//                           className="text-muted mb-2"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             d="M9 12h6m-6 4h6M9 8h1M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2h-3.5l-1-2h-5l-1 2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
//                           />
//                         </svg>
//                         <h6 className="mb-1 text-muted fw-semibold">
//                           No Record Found
//                         </h6>
//                         <p className="mb-0 text-muted small">
//                           No verification data has been submitted for this form
//                           yet.
//                         </p>
//                       </div>
//                     )}

//                     {Filtered_form1Fields?.length > 0 &&
//                       Filtered_form1Fields?.sort(
//                         (a, b) => a.display_order - b.display_order,
//                       )?.map((field) => (
//                         <div className="col-md-4 mb-3" key={field.field_id}>
//                           <label className="form-label fw-semibold">
//                             {field.label}
//                             {field.is_required && (
//                               <span className="text-danger ms-1">*</span>
//                             )}
//                           </label>

//                           {/* Text */}
//                           {field.field_type === "text" && (
//                             <input
//                               type="text"
//                               className="form-control"
//                               value={field.value?.trim() || "Not Provided"}
//                               placeholder={field.placeholder}
//                               disabled
//                             />
//                           )}

//                           {/* Number */}
//                           {field.field_type === "number" && (
//                             <input
//                               type="number"
//                               className="form-control"
//                               value={field.value || ""}
//                               placeholder={
//                                 field.value ? field.placeholder : "Not Provided"
//                               }
//                               disabled
//                             />
//                           )}

//                           {/* Dropdown */}
//                           {field.field_type === "dropdown" && (
//                             // <select
//                             //   className="form-select"
//                             //   value={field.value || ""}
//                             //   disabled
//                             // >
//                             //   <option value="">
//                             //     {field.placeholder || "Select"}
//                             //   </option>

//                             //   {field.options?.map((option: any, index: number) => {
//                             //     if (typeof option === "string") {
//                             //       return (
//                             //         <option key={option} value={option}>
//                             //           {option}
//                             //         </option>
//                             //       );
//                             //     }

//                             //     return (
//                             //       <option
//                             //         key={option.value ?? index}
//                             //         value={option.value ?? ""}
//                             //       >
//                             //         {option.label ?? option.value}
//                             //       </option>
//                             //     );
//                             //   })}
//                             // </select>
//                             <select
//                               className="form-select"
//                               value={field.value || ""}
//                               disabled
//                             >
//                               <option value="">Not Selected</option>

//                               {field.options?.map(
//                                 (option: any, index: number) => {
//                                   if (typeof option === "string") {
//                                     return (
//                                       <option key={option} value={option}>
//                                         {option}
//                                       </option>
//                                     );
//                                   }

//                                   return (
//                                     <option
//                                       key={option.value ?? index}
//                                       value={option.value ?? ""}
//                                     >
//                                       {option.label ?? option.value}
//                                     </option>
//                                   );
//                                 },
//                               )}
//                             </select>
//                           )}
//                         </div>
//                       ))}
//                   </div>
//                 </div>
//               )}
//             </div>

//             {/* Uploaded Documents — sirf tab dikhega jab documents hon */}
//             {getTemplateForm.loading ? (
//               <SnipperLoader />
//             ) : showDocsCard ? (
//               <div className="card mt-3">
//                 <div className="card-header bg-white py-3 border-bottom">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-file-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Uploaded Documents</h6>
//                       <small className="text-muted">
//                         Candidate uploaded supporting documents
//                       </small>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="card-body">
//                   <div className="row g-3">
//                     {docsInfo?.map((item) => (
//                       <div
//                         className="col-xl-3 col-lg-4 col-md-6"
//                         key={item.file_id}
//                       >
//                         <div
//                           className="card h-100 border-0 shadow-sm"
//                           style={{
//                             borderRadius: "14px",
//                             transition: "0.25s ease",
//                           }}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.transform =
//                               "translateY(-4px)";
//                             e.currentTarget.style.boxShadow =
//                               "0 8px 20px rgba(0,0,0,0.12)";
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.transform = "translateY(0)";
//                             e.currentTarget.style.boxShadow = "";
//                           }}
//                         >
//                           <div className="card-body text-center">
//                             {/* Preview */}
//                             <div
//                               className="mb-3 overflow-hidden rounded-3 border"
//                               style={{
//                                 height: 150,
//                                 background: "#f8f9fa",
//                               }}
//                             >
//                               {item.mime_type?.startsWith("image/") ? (
//                                 <img
//                                   src={`${apibaseurl}/uploads/${item.s3_key}`}
//                                   alt={item.original_name}
//                                   className="w-100 h-100"
//                                   style={{
//                                     objectFit: "cover",
//                                   }}
//                                 />
//                               ) : (
//                                 <div className="d-flex align-items-center justify-content-center h-100">
//                                   <PdfIcon size={60} color="#D32F2F" />
//                                 </div>
//                               )}
//                             </div>

//                             {/* Document Name */}
//                             <h6
//                               className="fw-bold text-capitalize mb-2"
//                               title={item.field_key.replaceAll("_", " ")}
//                             >
//                               {item.field_key.replaceAll("_", " ")}
//                             </h6>

//                             {/* File Name */}
//                             <div className="small text-muted text-truncate mb-3">
//                               {item.original_name}
//                             </div>

//                             {/* Info */}
//                             <div className="d-flex justify-content-between align-items-center mb-2">
//                               <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                 {item.mime_type?.split("/")[1]?.toUpperCase()}
//                               </span>

//                               <span className="badge bg-light text-dark border">
//                                 {new Date(item.uploaded_at).toLocaleDateString()}
//                               </span>
//                             </div>

//                             {/* View Button */}
//                             <a
//                               href={`${apibaseurl}/uploads/${item.s3_key}`}
//                               target="_blank"
//                               rel="noopener noreferrer"
//                               className="btn btn-sm btn-primary w-100 mt-2"
//                             >
//                               <i className="ti ti-eye me-1"></i>
//                               View Document
//                             </a>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               </div>
//             ) : null}
//           </>
//         )}

//         <div
//           className={`modal fade ${showDocumentsModal ? "show d-block" : ""}`}
//           tabIndex={-1}
//           style={{
//             backgroundColor: showDocumentsModal
//               ? "rgba(0,0,0,0.5)"
//               : "transparent",
//           }}
//         >
//           <div className="modal-dialog modal-lg modal-dialog-centered">
//             <div className="modal-content border-0 shadow">
//               {/* Header */}
//               <div className="modal-header">
//                 <h5 className="modal-title fw-bold">
//                   <i className="ti ti-folder-open me-2 text-primary"></i>
//                   Uploaded Documents
//                 </h5>

//                 <button
//                   type="button"
//                   className="btn-close"
//                   onClick={() => setShowDocumentsModal(false)}
//                 ></button>
//               </div>

//               {/* Body */}
//               <div className="modal-body">
//                 {selectedDocuments.length > 0 ? (
//                   <div className="row g-3">
//                     {selectedDocuments.map((doc) => (
//                       <div className="col-md-6" key={doc.document_id}>
//                         <div className="card border-0 shadow-sm h-100">
//                           <div className="card-body">
//                             <div className="d-flex">
//                               {/* Icon */}
//                               <div
//                                 className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
//                                 style={{
//                                   width: 55,
//                                   height: 55,
//                                   flexShrink: 0,
//                                 }}
//                               >
//                                 <i className="ti ti-file-text text-danger fs-3"></i>
//                               </div>

//                               {/* Details */}
//                               <div className="flex-grow-1">
//                                 <h6
//                                   className="fw-bold mb-1 text-truncate"
//                                   title={doc.original_name}
//                                 >
//                                   {doc.original_name}
//                                 </h6>

//                                 <div className="mb-2">
//                                   <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                     {doc.mime_type}
//                                   </span>
//                                 </div>

//                                 <a
//                                   href={`${apibaseurl}/uploads/${doc.file_name}`}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="btn btn-sm btn-primary"
//                                 >
//                                   <i className="ti ti-eye me-1"></i>
//                                   View Document
//                                 </a>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <div className="text-center py-5">
//                     <i
//                       className="ti ti-folder-off text-secondary"
//                       style={{ fontSize: "48px" }}
//                     ></i>

//                     <h6 className="mt-3">No Documents Found</h6>

//                     <p className="text-muted mb-0">
//                       No supporting documents are available.
//                     </p>
//                   </div>
//                 )}
//               </div>

//               {/* Footer */}
//               <div className="modal-footer">
//                 <button
//                   className="btn btn-secondary"
//                   onClick={() => setShowDocumentsModal(false)}
//                 >
//                   Close
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default InsufficiencyDetails;


// import React, { useEffect, useState } from "react";
// import Datatable from "../../../../core/common/dataTable";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import { all_routes } from "../../../router/all_routes";
// import PdfIcon from "../../../../style/icon/svg/PdfIcon";
// import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
// import Footer from "../../../../core/common/Footer";
// import useApi from "../../../../core/customhook/useApi";
// import { apibaseurl } from "../../../../core/webservice/Webservice_url";
// import Swal from "sweetalert2";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
// import { formatDate } from "../../../../core/common/TabelValue";
// import { useLocation, useNavigate } from "react-router";
// import { PageLoader } from "../../../../core/common/loader/PageLoader";
// import Loading from "../../../../core/common/custom-loading/Loading";
// import AddressDetailsView from "./Addressdetailsview";


// const InsufficiencyDetails = () => {
//   const location = useLocation(); 
//   const [showGuide, setShowGuide] = useState<boolean>(true);
//   const [templateInfo, setTemplateInfo] = useState<any>(null);
//   const [docsInfo, setDocsInfo] = useState<any[]>([]);
//   const [fields, setFields] = useState<any[]>([]); 
//   const [documents, setDocuments] = useState<File[]>([]);
//   const [insuffHistroy, setInsuffHistory] = useState<File[]>([]);
//   const [showDocumentsModal, setShowDocumentsModal] = useState(false);
//   const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
//   const [addressData, setAddressData] = useState<any[]>([]);

//   const { assignment_template_id, assignment_id, case_id, candidate_id } =
//     location?.state || {};

//   const getTemplateForm = useApi(
//     `/api/v1/insufficiency/template/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const getTemplateDocument = useApi(
//     `/api/v1/insufficiency/documents/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const insuffHistoryAPI = useApi(
//     `/api/v1/insufficiency/check/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const getTemplate = async () => {
//     try {
//       const res = await getTemplateForm.callApi();

//       if (res?.success) {
//         const data = res.data;

//         // Store response details except fields
//         setTemplateInfo({
//           response_id: data.response_id,
//           assignment_template_id: data.assignment_template_id,
//           is_draft: data.is_draft,
//           submitted_at: data.submitted_at,
//           updated_at: data.updated_at,
//           check_name: data.check_name,
//           form_type: data.form_type,
//         });
//         setAddressData(Array.isArray(data.data) ? data.data : []);
//         setFields(
//           [...(data.fields || [])].sort(
//             (a, b) => a.display_order - b.display_order,
//           ),
//         );
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const getTemplateDocs = async () => {
//     try {
//       const res = await getTemplateDocument.callApi();

//       if (res?.success) {
//         setDocsInfo(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const insuffHistory = async () => {
//     try {
//       const res = await insuffHistoryAPI.callApi();

//       if (res?.success) {
//         setInsuffHistory(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   useEffect(() => {
//     if (!assignment_template_id) return;
//     getTemplate();
//     getTemplateDocs();
//     insuffHistory();
//   }, [assignment_template_id]);

//   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     if (!e.target.files) return;

//     setDocuments(Array.from(e.target.files));
//   };

//   const columns = [
//     {
//       title: "Case ID",
//       dataIndex: "case_id",
//       key: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a: any, b: any) =>
//         (a?.case_id || "").localeCompare(b?.case_id || ""),
//     },

//     {
//       title: "Insufficiency Raise Date",
//       dataIndex: "created_at",
//       key: "created_at",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//       sorter: (a: any, b: any) =>
//         new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
//     },

//     {
//       title: "Due Date",
//       dataIndex: "actor_due_date",
//       key: "actor_due_date",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//       sorter: (a: any, b: any) =>
//         new Date(a.actor_due_date).getTime() -
//         new Date(b.actor_due_date).getTime(),
//     },

//     {
//       title: "User Type",
//       dataIndex: "actor_type",
//       key: "actor_type",
//       onCell: () => cellWidth(150),
//       render: (actorType: string) => (
//         <span
//           className={`badge px-3 py-2 ${
//             actorType === "CANDIDATE"
//               ? "bg-secondary-subtle text-secondary-emphasis"
//               : "bg-primary-subtle text-primary-emphasis"
//           }`}
//           style={{
//             fontSize: "12px",
//             fontWeight: 700,
//             minWidth: "100px",
//           }}
//         >
//           {actorType}
//         </span>
//       ),
//       sorter: (a: any, b: any) =>
//         (a?.actor_type || "").localeCompare(b?.actor_type || ""),
//     },
//     {
//       title: "Raised By",
//       dataIndex: "actor_name",
//       key: "actor_name",
//       onCell: () => cellWidth(150),
//       render: (text: string) => <div className="text-capitalize">{text}</div>,
//       sorter: (a: any, b: any) =>
//         (a?.actor_name || "").localeCompare(b?.actor_name || ""),
//     },
//     {
//       title: "Remarks",
//       dataIndex: "remarks",
//       key: "remarks",
//       onCell: () => cellWidth(350),
//       render: (text: string) => text || "-",
//       sorter: (a: any, b: any) =>
//         (a?.remarks || "").localeCompare(b?.remarks || ""),
//     },
//     {
//       title: "Candidate Name",
//       dataIndex: "candidate_name",
//       key: "candidate_name",
//       onCell: () => cellWidth(180),
//       sorter: (a: any, b: any) =>
//         (a?.candidate_name || "").localeCompare(b?.candidate_name || ""),
//     },
//     {
//       title: "Documents",
//       dataIndex: "documents",
//       key: "documents",
//       align: "center",
//       width: 120,
//       render: (documents: any[], record: any) => (
//         <button
//           className="btn btn-sm"
//           title="View Documents"
//           onClick={() => {
//             setSelectedDocuments(documents);
//             setShowDocumentsModal(true);
//           }}
//         >
//           <i className="ti ti-eye text-primary fs-3"></i>
//         </button>
//       ),
//     },

//     // {
//     //   title: "Candidate Response",
//     //   dataIndex: "candidate_response",
//     //   key: "candidate_response",
//     //   onCell: () => cellWidth(300),
//     //   render: (text: string) => text || "-",
//     //   sorter: (a: any, b: any) =>
//     //     (a?.candidate_response || "").localeCompare(
//     //       b?.candidate_response || "",
//     //     ),
//     // },

//     // {
//     //   title: "Candidate Response Date",
//     //   dataIndex: "candidate_response_at",
//     //   key: "candidate_response_at",
//     //   onCell: () => cellWidth(180),
//     //   render: (value: string) => formatDate(value),

//     //   sorter: (a: any, b: any) =>
//     //     new Date(a.candidate_response_at || 0).getTime() -
//     //     new Date(b.candidate_response_at || 0).getTime(),
//     // },

//     // {
//     //   title: "Status",
//     //   dataIndex: "status",
//     //   key: "status",
//     //   align: "center",
//     //   width: 150,
//     //   render: (status: string) => (
//     //     <span className="text-dark fw-bold" style={{ fontSize: "13px" }}>
//     //       {status}
//     //     </span>
//     //   ),
//     //   sorter: (a: any, b: any) =>
//     //     (a?.status || "").localeCompare(b?.status || ""),
//     // },
//   ];

//   // breadcrumb data
//   const breadcrumbItem = [
//     // { label: "Live Case Progress" },
//     { label: "Case Details" },
//     { label: "Insufficiency Details" },
//   ];

//   // filterd Form fields
//   const Filtered_form1Fields = fields?.filter((field) =>
//     ["text", "number", "dropdown"]?.includes(field.field_type),
//   );

//   // AddressDetailsView sirf "Address Verification" check par dikhega
//   const isAddressCheck =
//     templateInfo?.check_name?.trim().toLowerCase() === "address verification";

//   return (
//     <div className="page-wrapper">
//       <div className="content px-2 px-md-4 pb-5">
//         {insuffHistoryAPI.loading ? (
//           <PageLoader />
//         ) : (
//           <>
//             {/* PageBreadcrumb */}
//             <PageBreadcrumb
//               title="Insufficieny Details"
//               breadcrumbs={breadcrumbItem}
//               showBackButton={true}
//               backRoute={-1}
//               // backRoute={() => navigate(-1)}
//               // showGuideButton={true}
//               // onGuideClick={() => setShowGuide((prev) => !prev)}
//             />

//             <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
//               <button
//                 className="btn border bg-white px-3"
//                 onClick={() => setShowGuide((prev) => !prev)}
//               >
//                 <i className="ti ti-info-circle-filled fs-16"></i> Guide
//               </button>
//             </div>
//             {showGuide && (
//               <div
//                 style={{
//                   overflow: "hidden",
//                   maxHeight: showGuide ? "600px" : "0px",
//                   transition: "all 0.35s ease-in-out",
//                 }}
//               >
//                 {/* Arrow */}
//                 <div className="text-end pe-4">
//                   <i
//                     className="ti ti-triangle-inverted-filled"
//                     style={{
//                       fontSize: "22px",
//                       color: "#d6d6d6",
//                       lineHeight: 0,
//                     }}
//                   />
//                 </div>

//                 {/* Card */}
//                 <div
//                   className="card border-0 shadow-sm"
//                   style={{
//                     borderRadius: "10px",
//                     background: "#fff",
//                   }}
//                 >
//                   <div className="card-body p-4 position-relative">
//                     {/* Close */}
//                     <button
//                       type="button"
//                       className="btn btn-sm btn-light position-absolute"
//                       style={{
//                         top: "15px",
//                         right: "15px",
//                         width: "30px",
//                         height: "30px",
//                         borderRadius: "50%",
//                       }}
//                       onClick={() => setShowGuide(false)}
//                     >
//                       <i className="ti ti-x"></i>
//                     </button>

//                     {/* Heading */}
//                     <div className="d-flex align-items-center mb-4">
//                       <div
//                         className="me-3 d-flex align-items-center justify-content-center"
//                         style={{
//                           width: "42px",
//                           height: "42px",
//                           borderRadius: "50%",
//                           background: "#fff3cd",
//                         }}
//                       >
//                         <i
//                           className="ti ti-alert-circle"
//                           style={{
//                             color: "#d97706",
//                             fontSize: "20px",
//                           }}
//                         />
//                       </div>

//                       <h5 className="mb-1 fw-bold">Insufficiency</h5>
//                     </div>

//                     {/* Form 1 */}
//                     <div className="mb-4">
//                       <h6 className="fw-semibold text-primary mb-2">Form 1</h6>

//                       <div className="alert alert-light text-dark border mb-0">
//                         <i className="ti ti-info-circle me-2 text-primary"></i>
//                         Please review the check(s) listed below before
//                         proceeding.
//                       </div>
//                     </div>

//                     {/* Form 2 */}
//                     <div>
//                       <h6 className="fw-semibold text-primary mb-2">Form 2</h6>

//                       <div className="alert alert-light text-dark border mb-3">
//                         <ul className="mb-0 ps-3">
//                           <li className="mb-2">
//                             Mention your remarks in{" "}
//                             <strong>Insufficiency Details</strong> section and
//                             provide the required documentation.Please review
//                             form 1 and the documents submitted by you and then
//                             submit the insufficiency by providing the
//                             information in the form below.
//                           </li>

//                           <li className="mb-2">
//                             In case you feel that insufficiency request is
//                             incorrect and the details are already being
//                             provided, click on the incorrect insuff button.
//                           </li>
//                         </ul>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Insufficiency History */}
//             <div className="card mt-3">
//               <div className="card-header d-flex justify-content-between align-items-center py-3">
//                 <h5 className="mb-0 fw-bold">
//                   <i className="ti ti-history me-2 text-primary"></i>
//                   Insufficiency History
//                 </h5>
//               </div>
//               <div className="card-body">
//                 {/* Table */}
//                 <div className="">
//                   <Datatable
//                     dataSource={insuffHistroy}
//                     columns={columns}
//                     Selection={false}
//                     onRow={(record: any) => ({
//                       style: {
//                         backgroundColor:
//                           record.actor_type === "CANDIDATE"
//                             ? "#f8f9fa"
//                             : "#ffffff",
//                       },
//                     })}
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* Form1 Details */}
//             <div className="card mt-3 ">
//               <div className="card-header bg-white py-3 border-bottom">
//                 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-clipboard-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Form - 1</h6>

//                       <small className="text-muted">
//                         Candidate submitted information for verification
//                       </small>
//                     </div>
//                   </div>

//                   <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle px-3 py-2">
//                     {templateInfo?.check_name}
//                   </span>
//                 </div>
//               </div>

//               {getTemplateForm.loading ? (
//                 <SnipperLoader />
//               ) : isAddressCheck ? (
//                 <AddressDetailsView data={addressData} />
//               ) : (
//                 <div className="card-body">
//                   <div className="row gx-3">
//                     {/* {fields
//                       ?.filter((field) =>
//                         ["text", "number", "dropdown"]?.includes(
//                           field.field_type,
//                         ),
//                       ) */}
//                     {/* {!Filtered_form1Fields?.length && <h6 className=" mb-1">No Record Found</h6> } */}
//                     {!Filtered_form1Fields?.length && (
//                       <div className="d-flex flex-column align-items-center justify-content-center py-4 text-center">
//                         <svg
//                           width="40"
//                           height="40"
//                           viewBox="0 0 24 24"
//                           fill="none"
//                           stroke="currentColor"
//                           strokeWidth="1.5"
//                           className="text-muted mb-2"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             d="M9 12h6m-6 4h6M9 8h1M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2h-3.5l-1-2h-5l-1 2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
//                           />
//                         </svg>
//                         <h6 className="mb-1 text-muted fw-semibold">
//                           No Record Found
//                         </h6>
//                         <p className="mb-0 text-muted small">
//                           No verification data has been submitted for this form
//                           yet.
//                         </p>
//                       </div>
//                     )}

//                     {Filtered_form1Fields?.length > 0 &&
//                       Filtered_form1Fields?.sort(
//                         (a, b) => a.display_order - b.display_order,
//                       )?.map((field) => (
//                         <div className="col-md-4 mb-3" key={field.field_id}>
//                           <label className="form-label fw-semibold">
//                             {field.label}
//                             {field.is_required && (
//                               <span className="text-danger ms-1">*</span>
//                             )}
//                           </label>

//                           {/* Text */}
//                           {field.field_type === "text" && (
//                             <input
//                               type="text"
//                               className="form-control"
//                               value={field.value?.trim() || "Not Provided"}
//                               placeholder={field.placeholder}
//                               disabled
//                             />
//                           )}

//                           {/* Number */}
//                           {field.field_type === "number" && (
//                             <input
//                               type="number"
//                               className="form-control"
//                               value={field.value || ""}
//                               placeholder={
//                                 field.value ? field.placeholder : "Not Provided"
//                               }
//                               disabled
//                             />
//                           )}

//                           {/* Dropdown */}
//                           {field.field_type === "dropdown" && (
//                             // <select
//                             //   className="form-select"
//                             //   value={field.value || ""}
//                             //   disabled
//                             // >
//                             //   <option value="">
//                             //     {field.placeholder || "Select"}
//                             //   </option>

//                             //   {field.options?.map((option: any, index: number) => {
//                             //     if (typeof option === "string") {
//                             //       return (
//                             //         <option key={option} value={option}>
//                             //           {option}
//                             //         </option>
//                             //       );
//                             //     }

//                             //     return (
//                             //       <option
//                             //         key={option.value ?? index}
//                             //         value={option.value ?? ""}
//                             //       >
//                             //         {option.label ?? option.value}
//                             //       </option>
//                             //     );
//                             //   })}
//                             // </select>
//                             <select
//                               className="form-select"
//                               value={field.value || ""}
//                               disabled
//                             >
//                               <option value="">Not Selected</option>

//                               {field.options?.map(
//                                 (option: any, index: number) => {
//                                   if (typeof option === "string") {
//                                     return (
//                                       <option key={option} value={option}>
//                                         {option}
//                                       </option>
//                                     );
//                                   }

//                                   return (
//                                     <option
//                                       key={option.value ?? index}
//                                       value={option.value ?? ""}
//                                     >
//                                       {option.label ?? option.value}
//                                     </option>
//                                   );
//                                 },
//                               )}
//                             </select>
//                           )}
//                         </div>
//                       ))}
//                   </div>
//                 </div>
//               )}
//             </div>
 
//             {getTemplateForm.loading ? (
//               <SnipperLoader />
//             ) : templateInfo?.check_name && !isAddressCheck ? (
//               <div className="card mt-3">
//                 {/* <div className="card-header bg-white py-3 border-bottom">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-file-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Uploaded Documents</h6>
//                       <small className="text-muted">
//                         Candidate uploaded supporting documents
//                       </small>
//                     </div>
//                   </div>
//                 </div> */}

//                 {getTemplateDocument?.loading ? (
//                   <Loading />
//                 ) : (
//                   <>
//                   <div className="card-body p-0">
//                     {docsInfo?.length > 0 ? (
//                       <>
//                       <div className="card-header bg-white py-3 border-bottom">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-file-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Uploaded Documents</h6>
//                       <small className="text-muted">
//                         Candidate uploaded supporting documents
//                       </small>
//                     </div>
//                   </div>
//                 </div>
//                       <div className="row g-3">
//                         {docsInfo?.map((item) => (
//                           <div
//                             className="col-xl-3 col-lg-4 col-md-6"
//                             key={item.file_id}
//                           >
//                             <div
//                               className="card h-100 border-0 shadow-sm"
//                               style={{
//                                 borderRadius: "14px",
//                                 transition: "0.25s ease",
//                               }}
//                               onMouseEnter={(e) => {
//                                 e.currentTarget.style.transform =
//                                   "translateY(-4px)";
//                                 e.currentTarget.style.boxShadow =
//                                   "0 8px 20px rgba(0,0,0,0.12)";
//                               }}
//                               onMouseLeave={(e) => {
//                                 e.currentTarget.style.transform =
//                                   "translateY(0)";
//                                 e.currentTarget.style.boxShadow = "";
//                               }}
//                             >
//                               <div className="card-body text-center">
//                                 {/* Icon */}
//                                 {/* <div
//                             className="mx-auto mb-3 d-flex align-items-center justify-content-center"
//                             style={{
//                               width: 70,
//                               height: 70,
//                               borderRadius: "50%",
//                               background: "#FDECEC",
//                             }}
//                           >
//                             <PdfIcon size={42} color="#D32F2F" />
//                           </div> */}

//                                 {/* Preview */}
//                                 <div
//                                   className="mb-3 overflow-hidden rounded-3 border"
//                                   style={{
//                                     height: 150,
//                                     background: "#f8f9fa",
//                                   }}
//                                 >
//                                   {item.mime_type?.startsWith("image/") ? (
//                                     <img
//                                       src={`${apibaseurl}/uploads/${item.s3_key}`}
//                                       alt={item.original_name}
//                                       className="w-100 h-100"
//                                       style={{
//                                         objectFit: "cover",
//                                       }}
//                                     />
//                                   ) : (
//                                     <div className="d-flex align-items-center justify-content-center h-100">
//                                       <PdfIcon size={60} color="#D32F2F" />
//                                     </div>
//                                   )}
//                                 </div>

//                                 {/* Document Name */}
//                                 <h6
//                                   className="fw-bold text-capitalize mb-2"
//                                   title={item.field_key.replaceAll("_", " ")}
//                                 >
//                                   {item.field_key.replaceAll("_", " ")}
//                                 </h6>

//                                 {/* File Name */}
//                                 <div className="small text-muted text-truncate mb-3">
//                                   {item.original_name}
//                                 </div>

//                                 {/* Info */}
//                                 <div className="d-flex justify-content-between align-items-center mb-2">
//                                   <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                     {item.mime_type
//                                       .split("/")[1]
//                                       ?.toUpperCase()}
//                                   </span>

//                                   <span className="badge bg-light text-dark border">
//                                     {new Date(
//                                       item.uploaded_at,
//                                     ).toLocaleDateString()}
//                                   </span>
//                                 </div>

//                                 {/* View Button */}
//                                 <a
//                                   href={`${apibaseurl}/uploads/${item.s3_key}`}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="btn btn-sm btn-primary w-100 mt-2"
//                                 >
//                                   <i className="ti ti-eye me-1"></i>
//                                   View Document
//                                 </a>
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                       </>
//                     ) : (
//                       // <div className="d-flex flex-column align-items-center justify-content-center py-4 text-center">
//                       //   <i
//                       //     className="ti ti-folder-off mb-2"
//                       //     style={{ fontSize: "40px", color: "#6c757d" }}
//                       //   ></i>

//                       //   <h6 className="mb-1 text-muted fw-semibold">
//                       //     No Documents Found
//                       //   </h6>

//                       //   <p className="mb-0 text-muted small">
//                       //     No uploaded documents are available for this
//                       //     candidate.
//                       //   </p>
//                       // </div>
//                       <>
//                       </>
//                     )}
//                   </div>
//                   </>
//                 )}
//               </div>
//             ) : (
//               " "
//             )}
//           </>
//         )}

//         <div
//           className={`modal fade ${showDocumentsModal ? "show d-block" : ""}`}
//           tabIndex={-1}
//           style={{
//             backgroundColor: showDocumentsModal
//               ? "rgba(0,0,0,0.5)"
//               : "transparent",
//           }}
//         >
//           <div className="modal-dialog modal-lg modal-dialog-centered">
//             <div className="modal-content border-0 shadow">
//               {/* Header */}
//               <div className="modal-header">
//                 <h5 className="modal-title fw-bold">
//                   <i className="ti ti-folder-open me-2 text-primary"></i>
//                   Uploaded Documents
//                 </h5>

//                 <button
//                   type="button"
//                   className="btn-close"
//                   onClick={() => setShowDocumentsModal(false)}
//                 ></button>
//               </div>

//               {/* Body */}
//               <div className="modal-body">
//                 {selectedDocuments.length > 0 ? (
//                   <div className="row g-3">
//                     {selectedDocuments.map((doc) => (
//                       <div className="col-md-6" key={doc.document_id}>
//                         <div className="card border-0 shadow-sm h-100">
//                           <div className="card-body">
//                             <div className="d-flex">
//                               {/* Icon */}
//                               <div
//                                 className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
//                                 style={{
//                                   width: 55,
//                                   height: 55,
//                                   flexShrink: 0,
//                                 }}
//                               >
//                                 <i className="ti ti-file-text text-danger fs-3"></i>
//                               </div>

//                               {/* Details */}
//                               <div className="flex-grow-1">
//                                 <h6
//                                   className="fw-bold mb-1 text-truncate"
//                                   title={doc.original_name}
//                                 >
//                                   {doc.original_name}
//                                 </h6>

//                                 <div className="mb-2">
//                                   <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                     {doc.mime_type}
//                                   </span>
//                                 </div>

//                                 <a
//                                   href={`${apibaseurl}/uploads/${doc.file_name}`}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="btn btn-sm btn-primary"
//                                 >
//                                   <i className="ti ti-eye me-1"></i>
//                                   View Document
//                                 </a>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <div className="text-center py-5">
//                     <i
//                       className="ti ti-folder-off text-secondary"
//                       style={{ fontSize: "48px" }}
//                     ></i>

//                     <h6 className="mt-3">No Documents Found</h6>

//                     <p className="text-muted mb-0">
//                       No supporting documents are available.
//                     </p>
//                   </div>
//                 )}
//               </div>

//               {/* Footer */}
//               <div className="modal-footer">
//                 <button
//                   className="btn btn-secondary"
//                   onClick={() => setShowDocumentsModal(false)}
//                 >
//                   Close
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default InsufficiencyDetails;

// import React, { useEffect, useState } from "react";
// import Datatable from "../../../../core/common/dataTable";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import { all_routes } from "../../../router/all_routes";
// import PdfIcon from "../../../../style/icon/svg/PdfIcon";
// import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
// import Footer from "../../../../core/common/Footer";
// import useApi from "../../../../core/customhook/useApi";
// import { apibaseurl } from "../../../../core/webservice/Webservice_url";
// import Swal from "sweetalert2";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
// import { formatDate } from "../../../../core/common/TabelValue";
// import { useLocation, useNavigate } from "react-router";
// import { PageLoader } from "../../../../core/common/loader/PageLoader";
// import Loading from "../../../../core/common/custom-loading/Loading";
// import AddressDetailsView from "./Addressdetailsview";

// const InsufficiencyDetails = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [showGuide, setShowGuide] = useState<boolean>(true);
//   const [templateInfo, setTemplateInfo] = useState<any>(null);
//   const [docsInfo, setDocsInfo] = useState<any[]>([]);
//   const [fields, setFields] = useState<any[]>([]);
//   const [remarks, setRemarks] = useState("");
//   const [documents, setDocuments] = useState<File[]>([]);
//   const [insuffHistroy, setInsuffHistory] = useState<File[]>([]);
//   const [showDocumentsModal, setShowDocumentsModal] = useState(false);
//   const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
//   const [addressData, setAddressData] = useState<any[]>([]);

//   const { assignment_template_id, assignment_id, case_id, candidate_id } =
//     location?.state || {};

//   const getTemplateForm = useApi(
//     `/api/v1/insufficiency/template/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const getTemplateDocument = useApi(
//     `/api/v1/insufficiency/documents/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const insuffHistoryAPI = useApi(
//     `/api/v1/insufficiency/check/${assignment_template_id}`,
//     {
//       autoCall: false,
//       method: "GET",
//       handleGlobalError: false,
//     },
//   );

//   const getTemplate = async () => {
//     try {
//       const res = await getTemplateForm.callApi();

//       if (res?.success) {
//         const data = res.data;

//         setTemplateInfo({
//           response_id: data.response_id,
//           assignment_template_id: data.assignment_template_id,
//           is_draft: data.is_draft,
//           submitted_at: data.submitted_at,
//           updated_at: data.updated_at,
//           check_name: data.check_name,
//           form_type: data.form_type,
//         });

//         setAddressData(Array.isArray(data.data) ? data.data : []);
//         setFields(
//           [...(data.fields || [])].sort(
//             (a, b) => a.display_order - b.display_order,
//           ),
//         );
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const getTemplateDocs = async () => {
//     try {
//       const res = await getTemplateDocument.callApi();

//       if (res?.success) {
//         setDocsInfo(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const insuffHistory = async () => {
//     try {
//       const res = await insuffHistoryAPI.callApi();

//       if (res?.success) {
//         setInsuffHistory(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   useEffect(() => {
//     if (!assignment_template_id) return;
//     getTemplate();
//     getTemplateDocs();
//     insuffHistory();
//   }, [assignment_template_id]);

//   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     if (!e.target.files) return;

//     setDocuments(Array.from(e.target.files));
//   };

//   const columns = [
//     {
//       title: "Case ID",
//       dataIndex: "case_id",
//       key: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a: any, b: any) =>
//         (a?.case_id || "").localeCompare(b?.case_id || ""),
//     },

//     {
//       title: "Insufficiency Raise Date",
//       dataIndex: "created_at",
//       key: "created_at",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//       sorter: (a: any, b: any) =>
//         new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
//     },

//     {
//       title: "Due Date",
//       dataIndex: "actor_due_date",
//       key: "actor_due_date",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//       sorter: (a: any, b: any) =>
//         new Date(a.actor_due_date).getTime() -
//         new Date(b.actor_due_date).getTime(),
//     },

//     {
//       title: "User Type",
//       dataIndex: "actor_type",
//       key: "actor_type",
//       onCell: () => cellWidth(150),
//       render: (actorType: string) => (
//         <span
//           className={`badge px-3 py-2 ${
//             actorType === "CANDIDATE"
//               ? "bg-secondary-subtle text-secondary-emphasis"
//               : "bg-primary-subtle text-primary-emphasis"
//           }`}
//           style={{
//             fontSize: "12px",
//             fontWeight: 700,
//             minWidth: "100px",
//           }}
//         >
//           {actorType}
//         </span>
//       ),
//       sorter: (a: any, b: any) =>
//         (a?.actor_type || "").localeCompare(b?.actor_type || ""),
//     },
//     {
//       title: "Raised By",
//       dataIndex: "actor_name",
//       key: "actor_name",
//       onCell: () => cellWidth(150),
//       render: (text: string) => <div className="text-capitalize">{text}</div>,
//       sorter: (a: any, b: any) =>
//         (a?.actor_name || "").localeCompare(b?.actor_name || ""),
//     },
//     {
//       title: "Remarks",
//       dataIndex: "remarks",
//       key: "remarks",
//       onCell: () => cellWidth(350),
//       render: (text: string) => text || "-",
//       sorter: (a: any, b: any) =>
//         (a?.remarks || "").localeCompare(b?.remarks || ""),
//     },
//     {
//       title: "Candidate Name",
//       dataIndex: "candidate_name",
//       key: "candidate_name",
//       onCell: () => cellWidth(180),
//       sorter: (a: any, b: any) =>
//         (a?.candidate_name || "").localeCompare(b?.candidate_name || ""),
//     },
//     {
//       title: "Documents",
//       dataIndex: "documents",
//       key: "documents",
//       align: "center",
//       width: 120,
//       render: (documents: any[], record: any) => (
//         <button
//           className="btn btn-sm"
//           title="View Documents"
//           onClick={() => {
//             setSelectedDocuments(documents);
//             setShowDocumentsModal(true);
//           }}
//         >
//           <i className="ti ti-eye text-primary fs-3"></i>
//         </button>
//       ),
//     },

//     // {
//     //   title: "Candidate Response",
//     //   dataIndex: "candidate_response",
//     //   key: "candidate_response",
//     //   onCell: () => cellWidth(300),
//     //   render: (text: string) => text || "-",
//     //   sorter: (a: any, b: any) =>
//     //     (a?.candidate_response || "").localeCompare(
//     //       b?.candidate_response || "",
//     //     ),
//     // },

//     // {
//     //   title: "Candidate Response Date",
//     //   dataIndex: "candidate_response_at",
//     //   key: "candidate_response_at",
//     //   onCell: () => cellWidth(180),
//     //   render: (value: string) => formatDate(value),

//     //   sorter: (a: any, b: any) =>
//     //     new Date(a.candidate_response_at || 0).getTime() -
//     //     new Date(b.candidate_response_at || 0).getTime(),
//     // },

//     // {
//     //   title: "Status",
//     //   dataIndex: "status",
//     //   key: "status",
//     //   align: "center",
//     //   width: 150,
//     //   render: (status: string) => (
//     //     <span className="text-dark fw-bold" style={{ fontSize: "13px" }}>
//     //       {status}
//     //     </span>
//     //   ),
//     //   sorter: (a: any, b: any) =>
//     //     (a?.status || "").localeCompare(b?.status || ""),
//     // },
//   ];

//   // breadcrumb data
//   const breadcrumbItem = [
//     // { label: "Live Case Progress" },
//     { label: "Case Details" },
//     { label: "Insufficiency Details" },
//   ];

//   // filterd Form fields
//   const Filtered_form1Fields = fields?.filter((field) =>
//     ["text", "number", "dropdown"]?.includes(field.field_type),
//   );

//   const isAddressCheck =
//   templateInfo?.check_name?.trim().toLowerCase() === "address verification";

//   return (
//     <div className="page-wrapper">
//       <div className="content px-2 px-md-4 pb-5">
//         {insuffHistoryAPI.loading ? (
//           <PageLoader />
//         ) : (
//           <>
//             {/* PageBreadcrumb */}
//             <PageBreadcrumb
//               title="Insufficieny Details"
//               breadcrumbs={breadcrumbItem}
//               showBackButton={true}
//               backRoute={-1}
//               // backRoute={() => navigate(-1)}
//               // showGuideButton={true}
//               // onGuideClick={() => setShowGuide((prev) => !prev)}
//             />

//             <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
//               <button
//                 className="btn border bg-white px-3"
//                 onClick={() => setShowGuide((prev) => !prev)}
//               >
//                 <i className="ti ti-info-circle-filled fs-16"></i> Guide
//               </button>
//             </div>
//             {showGuide && (
//               <div
//                 style={{
//                   overflow: "hidden",
//                   maxHeight: showGuide ? "600px" : "0px",
//                   transition: "all 0.35s ease-in-out",
//                 }}
//               >
//                 {/* Arrow */}
//                 <div className="text-end pe-4">
//                   <i
//                     className="ti ti-triangle-inverted-filled"
//                     style={{
//                       fontSize: "22px",
//                       color: "#d6d6d6",
//                       lineHeight: 0,
//                     }}
//                   />
//                 </div>

//                 {/* Card */}
//                 <div
//                   className="card border-0 shadow-sm"
//                   style={{
//                     borderRadius: "10px",
//                     background: "#fff",
//                   }}
//                 >
//                   <div className="card-body p-4 position-relative">
//                     {/* Close */}
//                     <button
//                       type="button"
//                       className="btn btn-sm btn-light position-absolute"
//                       style={{
//                         top: "15px",
//                         right: "15px",
//                         width: "30px",
//                         height: "30px",
//                         borderRadius: "50%",
//                       }}
//                       onClick={() => setShowGuide(false)}
//                     >
//                       <i className="ti ti-x"></i>
//                     </button>

//                     {/* Heading */}
//                     <div className="d-flex align-items-center mb-4">
//                       <div
//                         className="me-3 d-flex align-items-center justify-content-center"
//                         style={{
//                           width: "42px",
//                           height: "42px",
//                           borderRadius: "50%",
//                           background: "#fff3cd",
//                         }}
//                       >
//                         <i
//                           className="ti ti-alert-circle"
//                           style={{
//                             color: "#d97706",
//                             fontSize: "20px",
//                           }}
//                         />
//                       </div>

//                       <h5 className="mb-1 fw-bold">Insufficiency</h5>
//                     </div>

//                     {/* Form 1 */}
//                     <div className="mb-4">
//                       <h6 className="fw-semibold text-primary mb-2">Form 1</h6>

//                       <div className="alert alert-light text-dark border mb-0">
//                         <i className="ti ti-info-circle me-2 text-primary"></i>
//                         Please review the check(s) listed below before
//                         proceeding.
//                       </div>
//                     </div>

//                     {/* Form 2 */}
//                     <div>
//                       <h6 className="fw-semibold text-primary mb-2">Form 2</h6>

//                       <div className="alert alert-light text-dark border mb-3">
//                         <ul className="mb-0 ps-3">
//                           <li className="mb-2">
//                             Mention your remarks in{" "}
//                             <strong>Insufficiency Details</strong> section and
//                             provide the required documentation.Please review
//                             form 1 and the documents submitted by you and then
//                             submit the insufficiency by providing the
//                             information in the form below.
//                           </li>

//                           <li className="mb-2">
//                             In case you feel that insufficiency request is
//                             incorrect and the details are already being
//                             provided, click on the incorrect insuff button.
//                           </li>
//                         </ul>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}
//             {/* Insufficiency History */}
//             <div className="card mt-3">
//               <div className="card-header d-flex justify-content-between align-items-center py-3">
//                 <h5 className="mb-0 fw-bold">
//                   <i className="ti ti-history me-2 text-primary"></i>
//                   Insufficiency History
//                 </h5>
//               </div>
//               <div className="card-body">
//                 {/* Table */}
//                 <div className="">
//                   <Datatable
//                     dataSource={insuffHistroy}
//                     columns={columns}
//                     Selection={false}
//                     onRow={(record: any) => ({
//                       style: {
//                         backgroundColor:
//                           record.actor_type === "CANDIDATE"
//                             ? "#f8f9fa"
//                             : "#ffffff",
//                       },
//                     })}
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* Form1 Details */}
//             <div className="card mt-3 ">
//               <div className="card-header bg-white py-3 border-bottom">
//                 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-clipboard-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Form - 1</h6>

//                       <small className="text-muted">
//                         Candidate submitted information for verification
//                       </small>
//                     </div>
//                   </div>

//                   <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle px-3 py-2">
//                     {templateInfo?.check_name}
//                   </span>
//                 </div>
//               </div>

//               {/* {getTemplateForm?.loading ? (
//                 <Loading />
//               ) : ( */}
//               {getTemplateForm.loading ? (
//                 <SnipperLoader />
//               ) : templateInfo?.check_name ? (
//                 <div className="card-body">
//                   <div className="row gx-3">
//                     {!Filtered_form1Fields?.length && (
//                       <div className="d-flex flex-column align-items-center justify-content-center py-4 text-center">
//                         <svg
//                           width="40"
//                           height="40"
//                           viewBox="0 0 24 24"
//                           fill="none"
//                           stroke="currentColor"
//                           strokeWidth="1.5"
//                           className="text-muted mb-2"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             d="M9 12h6m-6 4h6M9 8h1M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2h-3.5l-1-2h-5l-1 2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
//                           />
//                         </svg>
//                         <h6 className="mb-1 text-muted fw-semibold">
//                           No Record Found
//                         </h6>
//                         <p className="mb-0 text-muted small">
//                           No verification data has been submitted for this form
//                           yet.
//                         </p>
//                       </div>
//                     )}

//                     {Filtered_form1Fields?.length > 0 &&
//                       Filtered_form1Fields?.sort(
//                         (a, b) => a.display_order - b.display_order,
//                       ).map((field) => (
//                         <div className="col-md-4 mb-3" key={field.field_id}>
//                           <label className="form-label fw-semibold">
//                             {field.label}
//                             {field.is_required && (
//                               <span className="text-danger ms-1">*</span>
//                             )}
//                           </label>

//                           {/* Text */}
//                           {field.field_type === "text" && (
//                             <input
//                               type="text"
//                               className="form-control"
//                               value={field.value?.trim() || "Not Provided"}
//                               placeholder={field.placeholder}
//                               disabled
//                             />
//                           )}

//                           {/* Number */}
//                           {field.field_type === "number" && (
//                             <input
//                               type="number"
//                               className="form-control"
//                               value={field.value || ""}
//                               placeholder={
//                                 field.value ? field.placeholder : "Not Provided"
//                               }
//                               disabled
//                             />
//                           )}

//                           {/* Dropdown */}
//                           {field.field_type === "dropdown" && (
//                             // <select
//                             //   className="form-select"
//                             //   value={field.value || ""}
//                             //   disabled
//                             // >
//                             //   <option value="">
//                             //     {field.placeholder || "Select"}
//                             //   </option>

//                             //   {field.options?.map((option: any, index: number) => {
//                             //     if (typeof option === "string") {
//                             //       return (
//                             //         <option key={option} value={option}>
//                             //           {option}
//                             //         </option>
//                             //       );
//                             //     }

//                             //     return (
//                             //       <option
//                             //         key={option.value ?? index}
//                             //         value={option.value ?? ""}
//                             //       >
//                             //         {option.label ?? option.value}
//                             //       </option>
//                             //     );
//                             //   })}
//                             // </select>
//                             <select
//                               className="form-select"
//                               value={field.value || ""}
//                               disabled
//                             >
//                               <option value="">Not Selected</option>

//                               {field.options?.map(
//                                 (option: any, index: number) => {
//                                   if (typeof option === "string") {
//                                     return (
//                                       <option key={option} value={option}>
//                                         {option}
//                                       </option>
//                                     );
//                                   }

//                                   return (
//                                     <option
//                                       key={option.value ?? index}
//                                       value={option.value ?? ""}
//                                     >
//                                       {option.label ?? option.value}
//                                     </option>
//                                   );
//                                 },
//                               )}
//                             </select>
//                           )}
//                         </div>
//                       ))}
//                   </div>
//                 </div>
//               ) : (
//                 // )}
//                 <AddressDetailsView data={addressData} />
//               )}
//             </div>
//             {getTemplateForm.loading ? (
//               <SnipperLoader />
//             ) : templateInfo?.check_name ? (
//               <div className="card mt-3">
//                 <div className="card-header bg-white py-3 border-bottom">
//                   <div className="d-flex align-items-center">
//                     <div
//                       className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                       style={{ width: 40, height: 40 }}
//                     >
//                       <i className="ti ti-file-text text-warning fs-3"></i>
//                     </div>

//                     <div>
//                       <h6 className="fw-bold mb-0">Uploaded Documents</h6>
//                       <small className="text-muted">
//                         Candidate uploaded supporting documents
//                       </small>
//                     </div>
//                   </div>
//                 </div>

//                 {getTemplateDocument?.loading ? (
//                   <Loading />
//                 ) : (
//                   <div className="card-body">
//                     {docsInfo?.length > 0 ? (
//                       <div className="row g-3">
//                         {docsInfo?.map((item) => (
//                           <div
//                             className="col-xl-3 col-lg-4 col-md-6"
//                             key={item.file_id}
//                           >
//                             <div
//                               className="card h-100 border-0 shadow-sm"
//                               style={{
//                                 borderRadius: "14px",
//                                 transition: "0.25s ease",
//                               }}
//                               onMouseEnter={(e) => {
//                                 e.currentTarget.style.transform =
//                                   "translateY(-4px)";
//                                 e.currentTarget.style.boxShadow =
//                                   "0 8px 20px rgba(0,0,0,0.12)";
//                               }}
//                               onMouseLeave={(e) => {
//                                 e.currentTarget.style.transform =
//                                   "translateY(0)";
//                                 e.currentTarget.style.boxShadow = "";
//                               }}
//                             >
//                               <div className="card-body text-center">
//                                 {/* Icon */}
//                                 {/* <div
//                             className="mx-auto mb-3 d-flex align-items-center justify-content-center"
//                             style={{
//                               width: 70,
//                               height: 70,
//                               borderRadius: "50%",
//                               background: "#FDECEC",
//                             }}
//                           >
//                             <PdfIcon size={42} color="#D32F2F" />
//                           </div> */}

//                                 {/* Preview */}
//                                 <div
//                                   className="mb-3 overflow-hidden rounded-3 border"
//                                   style={{
//                                     height: 150,
//                                     background: "#f8f9fa",
//                                   }}
//                                 >
//                                   {item.mime_type?.startsWith("image/") ? (
//                                     <img
//                                       src={`${apibaseurl}/uploads/${item.s3_key}`}
//                                       alt={item.original_name}
//                                       className="w-100 h-100"
//                                       style={{
//                                         objectFit: "cover",
//                                       }}
//                                     />
//                                   ) : (
//                                     <div className="d-flex align-items-center justify-content-center h-100">
//                                       <PdfIcon size={60} color="#D32F2F" />
//                                     </div>
//                                   )}
//                                 </div>

//                                 {/* Document Name */}
//                                 <h6
//                                   className="fw-bold text-capitalize mb-2"
//                                   title={item.field_key.replaceAll("_", " ")}
//                                 >
//                                   {item.field_key.replaceAll("_", " ")}
//                                 </h6>

//                                 {/* File Name */}
//                                 <div className="small text-muted text-truncate mb-3">
//                                   {item.original_name}
//                                 </div>

//                                 {/* Info */}
//                                 <div className="d-flex justify-content-between align-items-center mb-2">
//                                   <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                     {item.mime_type
//                                       .split("/")[1]
//                                       ?.toUpperCase()}
//                                   </span>

//                                   <span className="badge bg-light text-dark border">
//                                     {new Date(
//                                       item.uploaded_at,
//                                     ).toLocaleDateString()}
//                                   </span>
//                                 </div>

//                                 {/* View Button */}
//                                 <a
//                                   href={`${apibaseurl}/uploads/${item.s3_key}`}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="btn btn-sm btn-primary w-100 mt-2"
//                                 >
//                                   <i className="ti ti-eye me-1"></i>
//                                   View Document
//                                 </a>
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     ) : (
//                       <div className="d-flex flex-column align-items-center justify-content-center py-4 text-center">
//                         <i
//                           className="ti ti-folder-off mb-2"
//                           style={{ fontSize: "40px", color: "#6c757d" }}
//                         ></i>

//                         <h6 className="mb-1 text-muted fw-semibold">
//                           No Documents Found
//                         </h6>

//                         <p className="mb-0 text-muted small">
//                           No uploaded documents are available for this
//                           candidate.
//                         </p>
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </div>
//             ) : (
//               " "
//             )}
//           </>
//         )}

//         <div
//           className={`modal fade ${showDocumentsModal ? "show d-block" : ""}`}
//           tabIndex={-1}
//           style={{
//             backgroundColor: showDocumentsModal
//               ? "rgba(0,0,0,0.5)"
//               : "transparent",
//           }}
//         >
//           <div className="modal-dialog modal-lg modal-dialog-centered">
//             <div className="modal-content border-0 shadow">
//               {/* Header */}
//               <div className="modal-header">
//                 <h5 className="modal-title fw-bold">
//                   <i className="ti ti-folder-open me-2 text-primary"></i>
//                   Uploaded Documents
//                 </h5>

//                 <button
//                   type="button"
//                   className="btn-close"
//                   onClick={() => setShowDocumentsModal(false)}
//                 ></button>
//               </div>

//               {/* Body */}
//               <div className="modal-body">
//                 {selectedDocuments.length > 0 ? (
//                   <div className="row g-3">
//                     {selectedDocuments.map((doc) => (
//                       <div className="col-md-6" key={doc.document_id}>
//                         <div className="card border-0 shadow-sm h-100">
//                           <div className="card-body">
//                             <div className="d-flex">
//                               {/* Icon */}
//                               <div
//                                 className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
//                                 style={{
//                                   width: 55,
//                                   height: 55,
//                                   flexShrink: 0,
//                                 }}
//                               >
//                                 <i className="ti ti-file-text text-danger fs-3"></i>
//                               </div>

//                               {/* Details */}
//                               <div className="flex-grow-1">
//                                 <h6
//                                   className="fw-bold mb-1 text-truncate"
//                                   title={doc.original_name}
//                                 >
//                                   {doc.original_name}
//                                 </h6>

//                                 <div className="mb-2">
//                                   <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                     {doc.mime_type}
//                                   </span>
//                                 </div>

//                                 <a
//                                   href={`${apibaseurl}/uploads/${doc.file_name}`}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="btn btn-sm btn-primary"
//                                 >
//                                   <i className="ti ti-eye me-1"></i>
//                                   View Document
//                                 </a>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <div className="text-center py-5">
//                     <i
//                       className="ti ti-folder-off text-secondary"
//                       style={{ fontSize: "48px" }}
//                     ></i>

//                     <h6 className="mt-3">No Documents Found</h6>

//                     <p className="text-muted mb-0">
//                       No supporting documents are available.
//                     </p>
//                   </div>
//                 )}
//               </div>

//               {/* Footer */}
//               <div className="modal-footer">
//                 <button
//                   className="btn btn-secondary"
//                   onClick={() => setShowDocumentsModal(false)}
//                 >
//                   Close
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default InsufficiencyDetails;
